import * as React from 'react';
import s from './terminator.scss';
import type { ITerminatorSuggestion } from '../terminator-data';
import { getCtaUrl } from '../terminator-data';
import type { WithTranslation as WithTranslationProps } from 'react-i18next';
import { config } from '../../../../config';
import type { BiInjectedProps as InjectedBiLoggerProps } from '../../../../bi';
import { biLogger } from '../../../../bi';
import { withBiLogger } from '../../../../component-decorators/with-bi-logger';
import type { MarketplaceStore } from '../../../../component-decorators/with-marketplace-store';
import { withMarketplaceStore } from '../../../../component-decorators/with-marketplace-store';
import {
  appMarketTerminatorPresenteToUser,
  appMarketTerminatorLearnHowClick,
} from '@wix/bi-logger-market/v2';

import { Box, Heading, Text, Button } from '@wix/design-system';
import { composer } from '../../../../component-decorators/composer';

export const terminatorSuggestionToTerminatorProps = (
  term: string,
  suggestion: ITerminatorSuggestion,
  marketplaceStore,
  fullWidth,
  isMobile,
): ITerminatorProps => {
  const { titleKey, descriptionKey, iconUrl, ctaUrl, spec, regex } = suggestion;
  const msid = marketplaceStore.metaSiteId;
  const useIconUrl = iconUrl.startsWith('http')
    ? iconUrl
    : `${config.marketplaceStaticsUrl}/assets/images/terminator-icons/${iconUrl}`;
  return {
    term,
    titleKey,
    descriptionKey,
    iconUrl: useIconUrl,
    cta: () => window.open(getCtaUrl(suggestion, msid), '_blank'),
    experimentName: spec,
    fullWidth,
    isMobile,
  };
};

export interface ITerminatorProps
  extends Partial<WithTranslationProps>,
    Partial<InjectedBiLoggerProps> {
  marketplaceStore?: MarketplaceStore;
  term: string;
  titleKey: string;
  descriptionKey: string;
  iconUrl: string;
  cta: Function;
  experimentName: string;
  fullWidth?: boolean;
  isMobile?: boolean;
}

interface ITerminatorState {}

export const Terminator = composer()
  .withTranslation()
  .compose(({ t, ...props }: ITerminatorProps) => (
    <TerminatorComponent t={t} {...props} />
  ));

@withMarketplaceStore
@withBiLogger
class TerminatorComponent extends React.Component<
  ITerminatorProps,
  ITerminatorState
> {
  sendTerminatorRenderBiEvent() {
    const { term, experimentName, marketplaceStore } = this.props;
    biLogger.report(
      appMarketTerminatorPresenteToUser({
        experiment_name: experimentName,
        market: config.originForBI,
        msid: marketplaceStore.metaSiteId,
        search_term: term,
      }),
    );
  }

  sendTerminatorCtaClickBiEvent() {
    const { term, experimentName, marketplaceStore } = this.props;

    biLogger.report(
      appMarketTerminatorLearnHowClick({
        experiment_name: experimentName,
        market: config.originForBI,
        msid: marketplaceStore.metaSiteId,
        search_term: term,
      }),
    );
  }

  componentDidMount() {
    this.sendTerminatorRenderBiEvent();
  }

  shouldComponentUpdate(nextProps) {
    return this.props.term !== nextProps.term;
  }

  componentDidUpdate(prevProps) {
    this.sendTerminatorRenderBiEvent();
  }

  render() {
    const { titleKey, descriptionKey, iconUrl, cta, fullWidth, isMobile, t } =
      this.props;
    return (
      <Box
        align="space-between"
        className={s.terminator}
        dataHook="terminator"
        width={fullWidth ? '100%' : ''}
      >
        <Box direction="vertical">
          <Heading appearance="H2" dataHook="terminator-title">
            {t(titleKey)}
          </Heading>
          <Text
            tagName="div"
            className={s.description}
            dataHook="terminator-description"
          >
            {t(descriptionKey)}
          </Text>
          <Button
            onClick={() => {
              this.sendTerminatorCtaClickBiEvent();
              cta();
            }}
            className={s.cta}
            dataHook="terminator-cta"
          >
            {t('terminator.learnHow')}
          </Button>
        </Box>

        {!isMobile && (
          <img
            src={iconUrl}
            className={s.icon}
            alt=""
            data-testid="terminator-icon"
          />
        )}
      </Box>
    );
  }
}
